import React, { useContext } from "react";
import { useIntl } from "react-intl";
import { useLocation, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { Context as AuthContext } from "context/Auth";
import { useProfileChoiceDialog } from "context/ProfileChoiceDialog";
import { Button } from "components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogDescription,
    DialogTitle,
} from "components/ui/dialog";
import { canRegisterAsClient, canRegisterAsProvider } from "app/Authentication/Register/common/registerAccess";
import { saveRegisterReturnPath } from "app/Authentication/Register/common/registerReturnPath";
import { buildRoute, REGISTER_STEPS, ROUTES } from "routes";
import { DialogCard } from "components/ui/dialog-card";

type RegisterStepPath = typeof REGISTER_STEPS[keyof typeof REGISTER_STEPS];

const buildRegisterRoute = (step: RegisterStepPath, redirectTo?: string) => {
    const route = buildRoute.registerSub(step);
    if (!redirectTo) return route;

    return `${route}?redirect=${encodeURIComponent(redirectTo)}`;
};

const ProfileChoiceDialog: React.FC = () => {
    const { open, redirectTo, closeProfileChoice } = useProfileChoiceDialog();
    const auth = useContext(AuthContext);
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();
    const isRegisterRoute = useMatch({ path: ROUTES.REGISTER, end: true });
    const [searchParams] = useSearchParams();

    const routeRedirect = isRegisterRoute
        ? (searchParams.get("redirect") ?? searchParams.get("returnUrl") ?? undefined)
        : undefined;
    const effectiveRedirect = redirectTo ?? routeRedirect;
    const isOpen = open || !!isRegisterRoute;

    const roles = auth.account?.user.roles;
    const registerRoles = roles && roles.length > 0 ? roles : undefined;
    const showClientCta = canRegisterAsClient(registerRoles);
    const showProviderCta = canRegisterAsProvider(registerRoles);

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            closeProfileChoice();
            if (isRegisterRoute) navigate(ROUTES.HOME, { replace: true });
        }
    };

    const navigateToRegister = (step: RegisterStepPath) => {
        const returnPathname = isRegisterRoute ? ROUTES.HOME : location.pathname;
        const returnSearch = isRegisterRoute ? "" : location.search;
        saveRegisterReturnPath(returnPathname, returnSearch);
        navigate(buildRegisterRoute(step, effectiveRedirect));
        closeProfileChoice();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogCard closeLabel={intl.formatMessage({ id: "authentication.profile_choice.close" })}>
                <div className="flex max-w-80 flex-col">
                    <DialogTitle className="text-pretty font-nunito text-3xl font-black leading-12 text-gray-900">
                        {intl.formatMessage({ id: "authentication.profile_choice.title" })}
                    </DialogTitle>
                </div>

                <DialogDescription className="font-nunito text-base font-medium leading-6 text-slate-600">
                    {intl.formatMessage({ id: "authentication.profile_choice.description" })}
                </DialogDescription>

                <div className="flex flex-col gap-3">
                    {showProviderCta && (
                        <Button
                            type="button"
                            className="h-12 w-full rounded-2xl bg-indigo-600 font-nunito text-base font-bold text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500 motion-reduce:transition-none"
                            onClick={() => navigateToRegister(REGISTER_STEPS.PROVIDER)}
                        >
                            <span>{intl.formatMessage({ id: "authentication.profile_choice.provider_cta" })}</span>
                            <ArrowRight className="size-5" aria-hidden="true" />
                        </Button>
                    )}

                    {showClientCta && (
                        <Button
                            type="button"
                            className="h-12 w-full rounded-2xl bg-indigo-600 font-nunito text-base font-bold text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500 motion-reduce:transition-none"
                            onClick={() => navigateToRegister(REGISTER_STEPS.USER)}
                        >
                            <span>{intl.formatMessage({ id: "authentication.profile_choice.employer_cta" })}</span>
                            <ArrowRight className="size-5" aria-hidden="true" />
                        </Button>
                    )}
                </div>

                <DialogClose asChild>
                    <button
                        type="button"
                        className="self-center font-nunito text-base font-bold leading-6 text-indigo-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                        {intl.formatMessage({ id: "authentication.profile_choice.cancel" })}
                    </button>
                </DialogClose>
            </DialogCard>
        </Dialog>
    );
};

export default ProfileChoiceDialog;
